import React, { useState } from "react";
import { Sun, Cloud, Moon, Zap } from "lucide-react";
import Card from "./Card";
import Dialog from "./Dialog";
import { useLocalStorage } from "../hooks/useLocalStorage";

const GREETING_OPTIONS = [
  { key: "morning", label: "Good Morning, Class!", Icon: Sun },
  { key: "afternoon", label: "Good Afternoon, Class!", Icon: Cloud },
  { key: "evening", label: "Good Evening, Class!", Icon: Moon },
  { key: "energy", label: "Let's Learn, Class!", Icon: Zap },
];

function getDefaultGreeting() {
  const currentHour = new Date().getHours();
  if (currentHour >= 12 && currentHour < 17) return "afternoon";
  if (currentHour >= 17) return "evening";
  return "morning";
}

export default function WelcomeCard() {
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [customGreeting, setCustomGreeting] = useLocalStorage("welcomeCard.customGreeting", "");
  const [selectedGreeting, setSelectedGreeting] = useLocalStorage("welcomeCard.selectedGreeting", getDefaultGreeting());

  const currentOption = GREETING_OPTIONS.find(opt => opt.key === selectedGreeting);
  const displayText = customGreeting || currentOption.label;

  return (
    <>
      <Card title="" colorIndex={5} onEdit={() => setIsEditOpen(true)}>
        <div className="h-full flex flex-col justify-center items-center gap-4 lg:gap-6">
          <div className="flex items-center justify-center">
            <currentOption.Icon className="w-16 h-16 lg:w-20 lg:h-20 xl:w-24 xl:h-24 2xl:w-32 2xl:h-32 text-slate-600" />
          </div>
          <div className="text-center">
            <h1 className="text-3xl lg:text-4xl xl:text-5xl 2xl:text-6xl font-fredoka font-bold text-slate-900 drop-shadow-sm mb-2">
              {displayText}
            </h1>
            <p className="text-lg lg:text-xl xl:text-2xl 2xl:text-3xl text-slate-600 font-fredoka font-medium">
              Ready to learn?
            </p>
          </div>
        </div>
      </Card>

      <Dialog isOpen={isEditOpen} onClose={() => setIsEditOpen(false)} title="Edit Welcome Message">
        <div className="space-y-4">
          <div>
            <h3 className="font-bold mb-2">Choose Greeting</h3>
            <div className="grid grid-cols-2 gap-3">
              {GREETING_OPTIONS.map(({ key, label, Icon }) => (
                <button
                  key={key}
                  onClick={() => {
                    setSelectedGreeting(key);
                    setCustomGreeting(""); // Clear custom text when selecting preset
                  }}
                  className={`flex flex-col items-center gap-2 p-3 rounded-lg border-2 transition hover:scale-105 ${
                    selectedGreeting === key ? "bg-purple-100 border-purple-400" : "bg-slate-50 border-slate-200 hover:border-slate-300"
                  }`}
                >
                  <Icon className="w-6 h-6" />
                  <span className="font-bold text-sm">{label}</span>
                </button>
              ))}
            </div>
          </div>

          <div>
            <h3 className="font-bold mb-2">Custom Greeting</h3>
            <input
              type="text"
              placeholder="Enter custom greeting..."
              value={customGreeting}
              onChange={(e) => setCustomGreeting(e.target.value)}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:border-purple-400"
            />
            <p className="text-xs text-slate-500 mt-1">
              Leave empty to use selected greeting above
            </p>
          </div>

          <div>
            <button 
              className="w-full px-3 py-2 rounded-lg bg-purple-200 font-bold shadow hover:bg-purple-300 transition" 
              onClick={() => setIsEditOpen(false)}
            >
              Done
            </button>
          </div>
        </div>
      </Dialog>
    </>
  );
}
